import type { JSX } from "react";
import { cn } from "./cn";

export type TagVariant =
  | "default"
  | "gold"
  | "green"
  | "orange"
  | "blue"
  | "teal"
  | "purple"
  | "red";

export type TagMode = "solid" | "subtle";

export interface TagProps {
  readonly label: string;
  readonly variant?: TagVariant;
  /** solid = filled bg + white text, subtle = transparent bg + colored text */
  readonly mode?: TagMode;
}

const solidStyles: Readonly<Record<TagVariant, string>> = {
  default: "bg-surface-hover text-content",
  gold: "bg-amber text-on-bright",
  green: "bg-green text-on-bright",
  orange: "bg-accent text-on-bright",
  blue: "bg-blue text-on-bright",
  teal: "bg-[#0e7490] text-white",
  purple: "bg-purple text-white",
  red: "bg-red text-white",
};

const subtleStyles: Readonly<Record<TagVariant, string>> = {
  default: "bg-surface-hover text-content",
  gold: "bg-amber/20 text-amber",
  green: "bg-[#22C55E20] text-[#22C55E]",
  orange: "bg-[#FF840020] text-[#FF8400]",
  blue: "bg-[#0EA5E920] text-[#0EA5E9]",
  teal: "bg-[#0e7490]/20 text-[#0e7490]",
  purple: "bg-[#8B5CF620] text-[#8B5CF6]",
  red: "bg-red/20 text-red",
};

/**
 * Small tag/chip component for labels.
 * Supports multiple color variants and two modes:
 *  - solid: filled background with white text (default)
 *  - subtle: transparent tinted background with colored text (B1 design)
 */
export function Tag({ label, variant = "default", mode = "solid" }: TagProps): JSX.Element {
  const styles = mode === "subtle" ? subtleStyles : solidStyles;
  return (
    <span
      className={cn(
        mode === "subtle"
          ? "px-1.5 py-[2px] rounded-[4px] text-[9px] font-semibold"
          : "px-2.5 py-1 rounded-full text-[10px] font-medium",
        styles[variant],
      )}
    >
      {label}
    </span>
  );
}
